// Evaluate a content AccessRule against the signed-in user.
//
// The point of gating the GUIDE is that nobody is taught a button they can't
// click — a field leader reading about the company-wide Manpower grid, or a PM
// without Financials reading about cost-to-complete, learns to distrust the
// whole document.
//
// This MIRRORS the app's real rules rather than inventing a parallel model:
//   • Admin bypasses permission checks everywhere (PermissionsGuard on the API).
//   • "Office user" and "field-leader-only" are copied from App.tsx, including
//     the subtlety that reports.view does NOT make a field leader an office
//     user — the FieldLeader role can carry it incidentally.
// If those rules ever move, this has to move with them, or the guide starts
// describing navigation that isn't there.
import type { AccessRule } from '../content/types';

export interface TrainingViewer {
  roles: string[];
  permissions: string[];
  canViewAll: boolean;
  /** Admin "show project costs" toggle — Financials/Commitments need it AND a
   *  permission, so content about them is hidden when it's off. */
  costsEnabled: boolean;
  /** At least one live project assignment — gates the Today content. */
  hasActiveAssignments: boolean;
  /**
   * Force field-leader scope even for a reader who isn't one.
   *
   * The Field Leader track is scoped by TRACK, not by reader (product owner, 2026-07-28):
   * "the entire page should be dedicated to what they can see and not reference
   * things they will never see". They're the only role limited to their assigned
   * jobs, so an admin previewing that track needs to see the field leader's
   * world — otherwise the person deciding whether the guide is any good is
   * reading a version no field leader will ever get.
   */
  forceFieldLeaderScope?: boolean;
}

export function viewerFrom(
  me: {
    roles?: string[];
    permissions?: string[];
    canViewAll?: boolean;
    hasActiveAssignments?: boolean;
  } | undefined,
  costsEnabled: boolean,
): TrainingViewer {
  return {
    roles: me?.roles ?? [],
    permissions: me?.permissions ?? [],
    canViewAll: me?.canViewAll ?? false,
    costsEnabled,
    hasActiveAssignments: me?.hasActiveAssignments ?? false,
  };
}

const isAdmin = (v: TrainingViewer) => v.roles.includes('Admin');

/** Admin implies every permission, exactly as the API's guard does. */
export const can = (v: TrainingViewer, permission: string) =>
  isAdmin(v) || v.permissions.includes(permission);

/** Copied from App.tsx — a field leader is only "office" via projects.edit,
 *  view-all, or Admin. */
export const isOfficeUser = (v: TrainingViewer) =>
  isAdmin(v) || v.permissions.includes('projects.edit') || v.canViewAll;

/** True for the actual signed-in user. */
export const isFieldLeaderOnly = (v: TrainingViewer) =>
  v.roles.includes('FieldLeader') && !isOfficeUser(v);

/** What content filtering should assume — the reader's own scope, or the
 *  field-leader scope forced by the track being viewed. */
export const inFieldLeaderScope = (v: TrainingViewer) =>
  v.forceFieldLeaderScope === true || isFieldLeaderOnly(v);

/**
 * All present rules must pass (AND); within one list any entry is enough (OR).
 * An absent rule means "everyone signed in".
 */
export function allows(v: TrainingViewer, rule?: AccessRule): boolean {
  if (!rule) return true;
  if (rule.adminOnly && !isAdmin(v)) return false;
  if (rule.anyPermission?.length && !rule.anyPermission.some((p) => can(v, p))) return false;
  if (rule.allPermissions?.length && !rule.allPermissions.every((p) => can(v, p))) return false;
  // Roles are matched exactly (e.g. 'FieldLeader'), and Admin does NOT stand in
  // for a role — being an admin doesn't make content written for field leaders
  // relevant to you.
  if (rule.anyRole?.length && !rule.anyRole.some((r) => v.roles.includes(r))) return false;
  if (rule.hideFromFieldLeaderOnly && inFieldLeaderScope(v)) return false;
  if (rule.requiresCostsEnabled && !v.costsEnabled) return false;
  // ⚠️ Data, not a badge — and Admin does NOT bypass it, exactly as the app
  // does not. An admin with no jobs of their own has no Today tab either.
  if (rule.requiresActiveAssignment && !v.hasActiveAssignments) return false;
  return true;
}

/**
 * Which track to mark as "your role".
 *
 * ⚠️ NOT simply the first track whose `suggestedFor` passes, which is what this
 * was. Declaration order put the PM track first, and its rule is a PERMISSION
 * (`projects.edit`) — so it matched before any role-based rule could be reached:
 *   • an Admin was told they were a Project Manager, because Admin implies every
 *     permission (product owner, 2026-08-03: "I am the admin but it's showing me Project
 *     Manager");
 *   • so was a Project Engineer, who genuinely holds `projects.edit`.
 *
 * The fix is specificity, not reordering — reordering would just move the bug to
 * whoever ends up last. A rule naming a ROLE identifies the person; a rule naming
 * a PERMISSION only says what they can do, and several roles can do the same
 * thing. So role-based rules outrank permission-based ones, and `adminOnly`
 * outranks both because there is exactly one Admin.
 *
 * Ties are broken by declaration order, which is why the tier matters more than
 * the position. A track with no `suggestedFor` is never suggested — that's
 * deliberate for the ones covering several roles.
 */
export function suggestedTrack<T extends { suggestedFor?: AccessRule; access?: AccessRule }>(
  v: TrainingViewer,
  tracks: T[],
): T | undefined {
  const tier = (r: AccessRule) => (r.adminOnly ? 3 : r.anyRole?.length ? 2 : 1);
  let best: T | undefined;
  let bestTier = 0;
  for (const t of tracks) {
    if (!t.suggestedFor || !allows(v, t.suggestedFor)) continue;
    const score = tier(t.suggestedFor);
    if (score > bestTier) { best = t; bestTier = score; }
  }
  return best;
}

/** Drop topics, then sections that end up empty — an empty heading reads like
 *  something failed to load. */
export function visibleSections<T extends { access?: AccessRule; topics: { access?: AccessRule }[] }>(
  v: TrainingViewer,
  sections: T[],
): T[] {
  return sections
    .filter((s) => allows(v, s.access))
    .map((s) => ({ ...s, topics: s.topics.filter((t) => allows(v, t.access)) }))
    .filter((s) => s.topics.length > 0);
}
